import { ResourceNotFoundError } from '../../lib/errors';
import { handleSupabaseList, handleSupabaseSingle, supabase } from '../../lib/supabase-unified';
import { UserLesson } from '../../types';

export const getUserLesson = async (userId: string, lessonId: number): Promise<UserLesson | null> => {
  try {
    return await handleSupabaseSingle<UserLesson>(
      supabase
        .from('user_lesson_interactions')
        .select('*')
        .eq('user_id', userId)
        .eq('lesson_id', lessonId)
        .single() as any
    );
  } catch (error) {
    if (error instanceof ResourceNotFoundError) {
      return null;
    }
    throw error;
  }
};

export const createOrUpdateUserLesson = async (
  userId: string, 
  lessonId: number, 
  updates: Partial<Omit<UserLesson, 'id' | 'created_at' | 'user_id' | 'lesson_id'>>
): Promise<UserLesson | null> => {
  try {
    return await handleSupabaseSingle<UserLesson>(
      supabase
        .from('user_lesson_interactions')
        .upsert(
          {
            user_id: userId,
            lesson_id: lessonId,
            ...updates,
          },
          {
            onConflict: 'user_id,lesson_id',
          }
        )
        .select()
        .single() as any
    );
  } catch (error) {
    if (error instanceof ResourceNotFoundError) {
      return null;
    }
    throw error;
  }
};

export const toggleBookmark = async (userId: string, lessonId: number): Promise<boolean> => {
  const existing = await getUserLesson(userId, lessonId);
  const newBookmarkState = !existing?.is_bookmarked;

  const result = await createOrUpdateUserLesson(userId, lessonId, {
    is_bookmarked: newBookmarkState,
  });

  return result?.is_bookmarked || false;
};

export const updateLeftOffTime = async (
  userId: string,
  lessonId: number,
  seconds: number
): Promise<void> => {
  await createOrUpdateUserLesson(userId, lessonId, {
    left_off_at_seconds: seconds,
  });
};

export const markLessonOpened = async (userId: string, lessonId: number): Promise<void> => {
  await createOrUpdateUserLesson(userId, lessonId, {
    is_opened: true,
  });
};


export const updateLanguage = async (
  userId: string,
  lessonId: number,
  language: string
): Promise<void> => {
  await createOrUpdateUserLesson(userId, lessonId, {
    language,
  });
};

export const getUserLessonsForProgress = async (
  userId: string,
  lessonIds: number[]
): Promise<Record<number, UserLesson>> => {
  const result = await handleSupabaseList<UserLesson>(
    supabase
      .from('user_lesson_interactions')
      .select('*')
      .eq('user_id', userId)
      .in('lesson_id', lessonIds) as any
  );

  // Convert array to object keyed by lesson_id for easy lookup
  const userLessonsMap: Record<number, UserLesson> = {};
  result.forEach((userLesson) => {
    userLessonsMap[userLesson.lesson_id] = userLesson;
  });

  return userLessonsMap;
};